import React, { useState, useEffect } from 'react';
import api from '../services/api';
import Swal from 'sweetalert2';
import { obtenerFechaLocal, formatearFechaLegible } from '../utils/dateUtils';

const GestionGastos = ({ proyectoId, proyecto }) => {
  const [gastos, setGastos] = useState([]);
  const [cuentasGasto, setCuentasGasto] = useState([]);
  const [cuentasBancarias, setCuentasBancarias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mostrarFormulario, setMostrarFormulario] = useState(false);
  const [gastoEditando, setGastoEditando] = useState(null);
  const [filtros, setFiltros] = useState({
    tipo: '',
    fechaDesde: '',
    fechaHasta: ''
  });

  const [formulario, setFormulario] = useState({
    tipo: 'combustible',
    concepto: '',
    monto: '',
    fecha: obtenerFechaLocal(),
    responsable: '',
    factura: '',
    observaciones: '',
    cuenta_gasto_id: '',
  });

  const tiposGasto = [
  { valor: 'combustible', icono: '⛽', label: 'Combustible' },
  { valor: 'viatico', icono: '🚗', label: 'Viáticos' },
  { valor: 'comida', icono: '🍔', label: 'Alimentación' },
  { valor: 'alojamiento', icono: '🏨', label: 'Alojamiento' },
  { valor: 'herramienta', icono: '🔧', label: 'Herramientas' },
  { valor: 'transporte', icono: '🚚', label: 'Transporte' },
  { valor: 'material_menor', icono: '📦', label: 'Material Menor' },
  { valor: 'electricidad', icono: '⚡', label: 'Electricidad' },
  { valor: 'agua', icono: '💧', label: 'Agua' },
  { valor: 'internet_comunicaciones', icono: '📡', label: 'Internet y Comunicaciones' },
  { valor: 'otro', icono: '📄', label: 'Otros' }
];

  useEffect(() => {
    cargarGastos();
  }, [proyectoId, filtros]);

  const flattenCuentas = (arr) => {
    const result = [];
    for (const c of (arr || [])) {
      if (c.codigo?.startsWith('6') && c.acepta_movimientos) result.push(c);
      if (c.hijos?.length) result.push(...flattenCuentas(c.hijos));
    }
    return result;
  };

  useEffect(() => {
    api.get('/contabilidad/cuentas')
      .then(r => {
        const lista = r.data.cuentas || r.data || [];
        setCuentasGasto(flattenCuentas(lista));
      })
      .catch(() => {});
    api.get('/bancos/cuentas')
      .then(r => setCuentasBancarias(r.data.cuentas || []))
      .catch(() => {});
  }, []);

  const cargarGastos = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filtros.tipo) params.append('tipo', filtros.tipo);
      if (filtros.fechaDesde) params.append('desde', filtros.fechaDesde);
      if (filtros.fechaHasta) params.append('hasta', filtros.fechaHasta);

      const response = await api.get(`/proyectos/${proyectoId}/gastos?${params.toString()}`);
      setGastos(response.data);
    } catch (error) {
      console.error('Error al cargar gastos:', error);
      Swal.fire('Error', 'No se pudieron cargar los gastos', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormulario(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formulario.concepto || !formulario.monto || !formulario.fecha) {
      Swal.fire('Error', 'Por favor completa los campos obligatorios', 'warning');
      return;
    }

    // Edición: sin modal, sin cambio contable
    if (gastoEditando) {
      try {
        await api.put(`/gastos/${gastoEditando.id}`, formulario);
        Swal.fire('✅ Actualizado', 'Gasto actualizado correctamente', 'success');
        limpiarFormulario();
        cargarGastos();
        setMostrarFormulario(false);
      } catch (error) {
        console.error('Error al actualizar gasto:', error);
        Swal.fire('Error', 'No se pudo actualizar el gasto', 'error');
      }
      return;
    }

    // Nuevo gasto → modal método de pago
    const opcionesCuentas = cuentasBancarias
      .map(c => `<option value="${c.id}">${c.nombre} — ${c.banco}</option>`).join('');
    const sinCuentas = cuentasBancarias.length === 0;

    const { value: pago } = await Swal.fire({
      title: '¿Método de pago?',
      html: `
        <div style="text-align:left; margin-bottom:10px;">
          <label style="font-weight:600; display:block; margin-bottom:6px;">Método</label>
          <select id="swal-metodo" class="swal2-input" style="margin:0; width:100%;">
            <option value="efectivo">Efectivo</option>
            <option value="transferencia">Transferencia bancaria</option>
          </select>
        </div>
        <div id="swal-cuenta-wrap" style="text-align:left; display:none;">
          <label style="font-weight:600; display:block; margin-bottom:6px;">Cuenta bancaria</label>
          <select id="swal-cuenta" class="swal2-input" style="margin:0; width:100%;">
            ${opcionesCuentas}
          </select>
        </div>
        <div id="swal-sin-cuentas" style="display:none; background:#fef3c7; border:1px solid #f59e0b;
             border-radius:8px; padding:12px; text-align:left; font-size:13px; color:#92400e;">
          ⚠️ No tienes cuentas bancarias registradas.<br/>
          Ve a <strong>Banco / Tesorería</strong> para agregar una antes de usar transferencia.
        </div>
      `,
      didOpen: () => {
        document.getElementById('swal-metodo').addEventListener('change', e => {
          const esT = e.target.value === 'transferencia';
          document.getElementById('swal-cuenta-wrap').style.display = esT && !sinCuentas ? 'block' : 'none';
          document.getElementById('swal-sin-cuentas').style.display = esT && sinCuentas ? 'block' : 'none';
        });
      },
      preConfirm: () => {
        const metodo = document.getElementById('swal-metodo').value;
        if (metodo === 'transferencia' && sinCuentas) {
          Swal.showValidationMessage('Debes registrar una cuenta bancaria primero en Banco / Tesorería');
          return false;
        }
        return {
          metodo_pago: metodo,
          cuenta_bancaria_id: document.getElementById('swal-cuenta')?.value || null,
        };
      },
      showCancelButton: true,
      confirmButtonColor: '#10b981',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'Confirmar gasto',
      cancelButtonText: 'Cancelar',
    });

    if (!pago) return;

    try {
      await api.post(`/proyectos/${proyectoId}/gastos`, {
        ...formulario,
        metodo_pago: pago.metodo_pago,
        cuenta_bancaria_id: pago.cuenta_bancaria_id,
      });
      Swal.fire('✅ Registrado', 'Gasto registrado y asiento generado', 'success');
      limpiarFormulario();
      cargarGastos();
      setMostrarFormulario(false);
    } catch (error) {
      console.error('Error al guardar gasto:', error);
      Swal.fire('Error', 'No se pudo guardar el gasto', 'error');
    }
  };

  const editarGasto = (gasto) => {
    setGastoEditando(gasto);
    setFormulario({
      tipo: gasto.tipo,
      concepto: gasto.concepto,
      monto: gasto.monto,
      fecha: gasto.fecha,
      responsable: gasto.responsable || '',
      factura: gasto.factura || '',
      observaciones: gasto.observaciones || '',
      cuenta_gasto_id: gasto.cuenta_gasto_id || '',
    });
    setMostrarFormulario(true);
  };

  const eliminarGasto = async (id) => {
    const result = await Swal.fire({
      title: '¿Eliminar gasto?',
      text: 'Esta acción no se puede deshacer',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar'
    });

    if (result.isConfirmed) {
      try {
        await api.delete(`/gastos/${id}`);
        Swal.fire('✅ Eliminado', 'Gasto eliminado correctamente', 'success');
        cargarGastos();
      } catch (error) {
        console.error('Error al eliminar:', error);
        Swal.fire('Error', 'No se pudo eliminar el gasto', 'error');
      }
    }
  };

  const limpiarFormulario = () => {
    setFormulario({
      tipo: 'combustible',
      concepto: '',
      monto: '',
      fecha: obtenerFechaLocal(),
      responsable: '',
      factura: '',
      observaciones: '',
      cuenta_gasto_id: '',
    });
    setGastoEditando(null);
  };

  const getTipoInfo = (tipo) => {
    return tiposGasto.find(t => t.valor === tipo) || tiposGasto[tiposGasto.length - 1];
  };

  const getTotalGastos = () => {
    return gastos.reduce((sum, g) => sum + parseFloat(g.monto || 0), 0);
  };

  const formatMoney = (valor) => {
    return `RD$ ${parseFloat(valor || 0).toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

 const formatFecha = (fecha) => {
  if (!fecha) return 'N/A';
  return formatearFechaLegible(fecha);
};

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <h2 style={styles.title}>💰 Gestión de Gastos</h2>
          <p style={styles.subtitle}>{proyecto?.nombre}</p>
        </div>
        <button
          onClick={() => {
            limpiarFormulario();
            setMostrarFormulario(!mostrarFormulario);
          }}
          style={styles.btnPrimary}
        >
          {mostrarFormulario ? '✕ Cancelar' : '+ Registrar Gasto'}
        </button>
      </div>

      {/* Resumen */}
      <div style={styles.resumen}>
        <div style={styles.resumenCard}>
          <div style={styles.resumenIcon}>💸</div>
          <div>
            <p style={styles.resumenLabel}>Total Gastos</p>
            <h3 style={styles.resumenValue}>{formatMoney(getTotalGastos())}</h3>
          </div>
        </div>
        <div style={styles.resumenCard}>
          <div style={styles.resumenIcon}>📊</div>
          <div>
            <p style={styles.resumenLabel}>Gastos Registrados</p>
            <h3 style={styles.resumenValue}>{gastos.length}</h3>
          </div>
        </div>
      </div>

      {/* Formulario */}
      {mostrarFormulario && (
        <div style={styles.formulario}>
          <h3 style={styles.formTitle}>
            {gastoEditando ? '✏️ Editar Gasto' : '➕ Nuevo Gasto'}
          </h3>

          <form onSubmit={handleSubmit}>
            <div style={styles.formGrid}>
              {/* Tipo de Gasto */}
              <div style={styles.formGroup}>
                <label style={styles.label}>Tipo de Gasto *</label>
                <select
                  name="tipo"
                  value={formulario.tipo}
                  onChange={handleChange}
                  style={styles.select}
                  required
                >
                  {tiposGasto.map(tipo => (
                    <option key={tipo.valor} value={tipo.valor}>
                      {tipo.icono} {tipo.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Fecha */}
              <div style={styles.formGroup}>
                <label style={styles.label}>Fecha *</label>
                <input
                  type="date"
                  name="fecha"
                  value={formulario.fecha}
                  onChange={handleChange}
                  style={styles.input}
                  required
                />
              </div>

              {/* Cuenta de Gasto */}
              <div style={styles.formGroup}>
                <label style={styles.label}>Cuenta de Gasto (contable)</label>
                <select
                  name="cuenta_gasto_id"
                  value={formulario.cuenta_gasto_id}
                  onChange={handleChange}
                  style={styles.select}
                >
                  <option value="">— Selecciona cuenta (fallback: 6.2) —</option>
                  {cuentasGasto.map(c => (
                    <option key={c.id} value={c.id}>
                      {c.codigo} — {c.nombre}
                    </option>
                  ))}
                </select>
              </div>

              {/* Concepto */}
              <div style={{...styles.formGroup, gridColumn: '1 / -1'}}>
                <label style={styles.label}>Concepto *</label>
                <input
                  type="text"
                  name="concepto"
                  value={formulario.concepto}
                  onChange={handleChange}
                  placeholder="Ej: Gasolina para camión, Almuerzos del equipo..."
                  style={styles.input}
                  required
                />
              </div>

              {/* Monto */}
              <div style={styles.formGroup}>
                <label style={styles.label}>Monto (RD$) *</label>
                <input
                  type="number"
                  name="monto"
                  value={formulario.monto}
                  onChange={handleChange}
                  placeholder="0.00"
                  step="0.01"
                  min="0"
                  style={styles.input}
                  required
                />
              </div>

              {/* Responsable */}
              <div style={styles.formGroup}>
                <label style={styles.label}>Responsable</label>
                <input
                  type="text"
                  name="responsable"
                  value={formulario.responsable}
                  onChange={handleChange}
                  placeholder="Nombre del responsable"
                  style={styles.input}
                />
              </div>

              {/* Factura */}
              <div style={styles.formGroup}>
                <label style={styles.label}>No. Factura</label>
                <input
                  type="text"
                  name="factura"
                  value={formulario.factura}
                  onChange={handleChange}
                  placeholder="Número de factura"
                  style={styles.input}
                />
              </div>

              {/* Observaciones */}
              <div style={{...styles.formGroup, gridColumn: '1 / -1'}}>
                <label style={styles.label}>Observaciones</label>
                <textarea
                  name="observaciones"
                  value={formulario.observaciones}
                  onChange={handleChange}
                  placeholder="Detalles adicionales..."
                  style={{...styles.input, minHeight: '80px', resize: 'vertical'}}
                />
              </div>
            </div>

            {/* Botones */}
            <div style={styles.formActions}>
              <button
                type="button"
                onClick={() => {
                  limpiarFormulario();
                  setMostrarFormulario(false);
                }}
                style={styles.btnSecondary}
              >
                Cancelar
              </button>
              <button type="submit" style={styles.btnSubmit}>
                {gastoEditando ? '💾 Actualizar' : '💾 Guardar Gasto'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Filtros */}
      <div style={styles.filtros}>
        <select
          value={filtros.tipo}
          onChange={(e) => setFiltros({...filtros, tipo: e.target.value})}
          style={styles.filtroSelect}
        >
          <option value="">Todos los tipos</option>
          {tiposGasto.map(tipo => (
            <option key={tipo.valor} value={tipo.valor}>
              {tipo.icono} {tipo.label}
            </option>
          ))}
        </select>

        <input
          type="date"
          value={filtros.fechaDesde}
          onChange={(e) => setFiltros({...filtros, fechaDesde: e.target.value})}
          style={styles.filtroInput}
          placeholder="Desde"
        />

        <input
          type="date"
          value={filtros.fechaHasta}
          onChange={(e) => setFiltros({...filtros, fechaHasta: e.target.value})}
          style={styles.filtroInput}
          placeholder="Hasta"
        />

        {(filtros.tipo || filtros.fechaDesde || filtros.fechaHasta) && (
          <button
            onClick={() => setFiltros({ tipo: '', fechaDesde: '', fechaHasta: '' })}
            style={styles.btnLimpiar}
          >
            Limpiar filtros
          </button>
        )}
      </div>

      {/* Lista de Gastos */}
      {loading ? (
        <div style={styles.loading}>
          <div style={styles.spinner}></div>
          <p>Cargando gastos...</p>
        </div>
      ) : gastos.length === 0 ? (
        <div style={styles.empty}>
          <div style={styles.emptyIcon}>📋</div>
          <h3>No hay gastos registrados</h3>
          <p>Comienza registrando el primer gasto del proyecto</p>
        </div>
      ) : (
        <div style={styles.listaGastos}>
          {gastos.map((gasto) => {
            const tipoInfo = getTipoInfo(gasto.tipo);
            return (
              <div key={gasto.id} style={styles.gastoCard}>
                <div style={styles.gastoHeader}>
                  <div style={styles.gastoTipo}>
                    <span style={styles.gastoIcono}>{tipoInfo.icono}</span>
                    <div>
                      <h4 style={styles.gastoConcepto}>{gasto.concepto}</h4>
                      <p style={styles.gastoTipoLabel}>{tipoInfo.label}</p>
                    </div>
                  </div>
                  <div style={styles.gastoMonto}>{formatMoney(gasto.monto)}</div>
                </div>

                <div style={styles.gastoInfo}>
                  <div style={styles.gastoInfoItem}>
                    <span style={styles.infoLabel}>📅 Fecha:</span>
                    <span>{formatFecha(gasto.fecha)}</span>
                  </div>
                  {gasto.responsable && (
                    <div style={styles.gastoInfoItem}>
                      <span style={styles.infoLabel}>👤 Responsable:</span>
                      <span>{gasto.responsable}</span>
                    </div>
                  )}
                  {gasto.factura && (
                    <div style={styles.gastoInfoItem}>
                      <span style={styles.infoLabel}>🧾 Factura:</span>
                      <span>{gasto.factura}</span>
                    </div>
                  )}
                </div>

                {gasto.observaciones && (
                  <div style={styles.gastoObservaciones}>
                    <strong>Observaciones:</strong> {gasto.observaciones}
                  </div>
                )}

                <div style={styles.gastoActions}>
                  <button
                    onClick={() => editarGasto(gasto)}
                    style={styles.btnEdit}
                  >
                    ✏️ Editar
                  </button>
                  <button
                    onClick={() => eliminarGasto(gasto.id)}
                    style={styles.btnDelete}
                  >
                    🗑️ Eliminar
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    padding: '20px'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '20px',
    flexWrap: 'wrap',
    gap: '15px'
  },
  title: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#1e293b',
    margin: '0 0 5px 0'
  },
  subtitle: {
    fontSize: '14px',
    color: '#64748b',
    margin: 0
  },
  btnPrimary: {
    padding: '12px 24px',
    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)'
  },
  resumen: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '15px',
    marginBottom: '20px'
  },
  resumenCard: {
    display: 'flex',
    alignItems: 'center',
    gap: '15px',
    background: 'white',
    padding: '20px',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    border: '1px solid #e2e8f0'
  },
  resumenIcon: {
    fontSize: '36px'
  },
  resumenLabel: {
    fontSize: '13px',
    color: '#64748b',
    margin: '0 0 5px 0',
    fontWeight: '600'
  },
  resumenValue: {
    fontSize: '22px',
    fontWeight: '700',
    color: '#1e293b',
    margin: 0
  },
  formulario: {
    background: 'white',
    padding: '25px',
    borderRadius: '12px',
    marginBottom: '20px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    border: '1px solid #e2e8f0'
  },
  formTitle: {
    fontSize: '18px',
    fontWeight: '700',
    color: '#1e293b',
    marginBottom: '20px'
  },
  formGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '15px',
    marginBottom: '20px'
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column'
  },
  label: {
    fontSize: '13px',
    fontWeight: '600',
    color: '#475569',
    marginBottom: '6px'
  },
  input: {
    padding: '10px 12px',
    fontSize: '14px',
    border: '1px solid #cbd5e1',
    borderRadius: '8px',
    outline: 'none',
    transition: 'all 0.2s'
  },
  select: {
    padding: '10px 12px',
    fontSize: '14px',
    border: '1px solid #cbd5e1',
    borderRadius: '8px',
    outline: 'none',
    background: 'white',
    cursor: 'pointer'
  },
  formActions: {
    display: 'flex',
    gap: '10px',
    justifyContent: 'flex-end'
  },
  btnSecondary: {
    padding: '10px 20px',
    background: '#f1f5f9',
    color: '#64748b',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer'
  },
  btnSubmit: {
    padding: '10px 20px',
    background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)'
  },
  filtros: {
    display: 'flex',
    gap: '10px',
    marginBottom: '20px',
    flexWrap: 'wrap'
  },
  filtroSelect: {
    padding: '10px 12px',
    border: '1px solid #cbd5e1',
    borderRadius: '8px',
    fontSize: '14px',
    background: 'white',
    cursor: 'pointer'
  },
  filtroInput: {
    padding: '10px 12px',
    border: '1px solid #cbd5e1',
    borderRadius: '8px',
    fontSize: '14px'
  },
  btnLimpiar: {
    padding: '10px 16px',
    background: '#f1f5f9',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    color: '#64748b',
    cursor: 'pointer'
  },
  loading: {
    textAlign: 'center',
    padding: '40px'
  },
  spinner: {
    border: '4px solid #f3f4f6',
    borderTop: '4px solid #10b981',
    borderRadius: '50%',
    width: '40px',
    height: '40px',
    animation: 'spin 0.8s linear infinite',
    margin: '0 auto 15px'
  },
  empty: {
    textAlign: 'center',
    padding: '60px 20px',
    background: 'white',
    borderRadius: '12px',
    border: '2px dashed #cbd5e1'
  },
  emptyIcon: {
    fontSize: '64px',
    marginBottom: '15px'
  },
  listaGastos: {
    display: 'grid',
    gap: '15px'
  },
  gastoCard: {
    background: 'white',
    padding: '20px',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    border: '1px solid #e2e8f0'
  },
  gastoHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '15px',
    gap: '15px'
  },
  gastoTipo: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    flex: 1
  },
  gastoIcono: {
    fontSize: '32px'
  },
  gastoConcepto: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#1e293b',
    margin: '0 0 4px 0'
  },
  gastoTipoLabel: {
    fontSize: '12px',
    color: '#64748b',
    margin: 0
  },
  gastoMonto: {
    fontSize: '20px',
    fontWeight: '700',
    color: '#10b981'
  },
  gastoInfo: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '10px',
    marginBottom: '10px'
  },
  gastoInfoItem: {
    fontSize: '13px',
    color: '#475569'
  },
  infoLabel: {
    fontWeight: '600',
    marginRight: '5px'
  },
  gastoObservaciones: {
    fontSize: '13px',
    color: '#64748b',
    padding: '10px',
    background: '#f8fafc',
    borderRadius: '6px',
    marginBottom: '10px'
  },
  gastoActions: {
    display: 'flex',
    gap: '10px',
    paddingTop: '15px',
    borderTop: '1px solid #e2e8f0'
  },
  btnEdit: {
    flex: 1,
    padding: '8px 16px',
    background: '#f0f9ff',
    color: '#0369a1',
    border: '1px solid #bae6fd',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer'
  },
  btnDelete: {
    flex: 1,
    padding: '8px 16px',
    background: '#fef2f2',
    color: '#dc2626',
    border: '1px solid #fecaca',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer'
  }
};

export default GestionGastos;